import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { ComingSoonPublicItem } from '../../services/coming-soon-service'
import './coming-soon-band.scss'

export type ComingSoonBandTestIdPrefix = 'home-v2' | 'opportunities-v2'

export interface ComingSoonBandProps {
  items: ComingSoonPublicItem[]
  loading: boolean
  testIdPrefix: ComingSoonBandTestIdPrefix
}

const AUTO_ADVANCE_MS = 5000

function getChipLabel(item: ComingSoonPublicItem) {
  const title = item.title?.trim() ?? ''
  return title.length > 0 ? title : 'Coming soon'
}

function EarlyLookIntro() {
  return (
    <div className='opportunities-v2__early-look-intro'>
      <h2 className='opportunities-v2__coming-soon-eyebrow'>EARLY LOOK</h2>
    </div>
  )
}

export function ComingSoonBand({ items, loading, testIdPrefix }: ComingSoonBandProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const pauseAutoRef = useRef(false)

  const safeCount = items.length

  useEffect(() => {
    setActiveIndex((i) => {
      if (safeCount === 0) return 0
      return Math.min(i, safeCount - 1)
    })
  }, [safeCount])

  useEffect(() => {
    if (loading || safeCount <= 1) return

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return

    const tick = () => {
      if (!pauseAutoRef.current) {
        setActiveIndex((i) => (i + 1) % safeCount)
      }
    }
    const id = window.setInterval(tick, AUTO_ADVANCE_MS)
    return () => window.clearInterval(id)
  }, [loading, safeCount])

  const trackStyle = useMemo(() => {
    if (safeCount === 0) return undefined
    const slidePct = 100 / safeCount
    return {
      width: `${safeCount * 100}%`,
      transform: `translateX(-${slidePct * activeIndex}%)`,
    }
  }, [activeIndex, safeCount])

  const setPause = (paused: boolean) => {
    pauseAutoRef.current = paused
  }

  return (
    <section className='opportunities-v2__coming-soon' aria-label='Early look opportunities'>
      <div className='opportunities-v2__coming-soon-inner'>
        {loading ? (
          <div className='opportunities-v2__early-look-layout'>
            <EarlyLookIntro />
            <div className='opportunities-v2__early-look-carousel-col'>
              <p
                className='opportunities-v2__coming-soon-loading'
                data-testid={`${testIdPrefix}-coming-soon-loading`}
              >
                Loading…
              </p>
            </div>
          </div>
        ) : (
          <div className='opportunities-v2__early-look-layout'>
            <EarlyLookIntro />
            <div className='opportunities-v2__early-look-carousel-col'>
              <div
                className='opportunities-v2__early-look-carousel'
                role='region'
                aria-roledescription='carousel'
                aria-label='Upcoming opportunities preview'
                data-testid={`${testIdPrefix}-coming-soon-strip`}
                onMouseEnter={() => setPause(true)}
                onMouseLeave={() => setPause(false)}
                onFocusCapture={() => setPause(true)}
                onBlurCapture={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                    setPause(false)
                  }
                }}
              >
                <div className='opportunities-v2__early-look-viewport'>
                  <ul
                    className='opportunities-v2__early-look-track'
                    style={trackStyle}
                    aria-live='off'
                  >
                    {items.map((item, slideIndex) => {
                      const isActive = slideIndex === activeIndex
                      const slideBasisPct = safeCount > 0 ? 100 / safeCount : 100
                      return (
                        <li
                          key={item.id}
                          className='opportunities-v2__early-look-slide'
                          style={{ flex: `0 0 ${slideBasisPct}%` }}
                          aria-hidden={!isActive}
                        >
                          <article
                            className='opportunities-v2__soon-card'
                            data-testid={`${testIdPrefix}-coming-soon-card-${item.id}`}
                          >
                            <div className='opportunities-v2__soon-card-img-wrap'>
                              <img
                                src={item.coverImageUrl}
                                alt=''
                                className='opportunities-v2__soon-card-img'
                                loading={slideIndex === 0 ? 'eager' : 'lazy'}
                                width={320}
                                height={200}
                              />
                            </div>
                            <div className='opportunities-v2__soon-card-body'>
                              <span className='opportunities-v2__soon-chip'>
                                {getChipLabel(item)}
                              </span>
                              <p className='opportunities-v2__soon-card-desc'>{item.description}</p>
                            </div>
                          </article>
                        </li>
                      )
                    })}
                  </ul>
                </div>
                {safeCount > 1 ? (
                  <div
                    className='opportunities-v2__early-look-dots'
                    role='tablist'
                    aria-label='Select preview slide'
                  >
                    {items.map((item, i) => (
                      <button
                        key={item.id}
                        type='button'
                        role='tab'
                        aria-selected={i === activeIndex}
                        aria-label={`Slide ${i + 1} of ${safeCount}`}
                        className={
                          i === activeIndex
                            ? 'opportunities-v2__early-look-dot opportunities-v2__early-look-dot--active'
                            : 'opportunities-v2__early-look-dot'
                        }
                        data-testid={`${testIdPrefix}-early-look-dot-${item.id}`}
                        onClick={() => setActiveIndex(i)}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
